import React, { useContext } from 'react'
import Chart from 'react-apexcharts'
import Table from '../../component/table'
import { Button, TableColumnsType } from 'antd'
import { PageIndexContext } from '../../pages/graphic/index'
import { Record, Sensor } from '../../types'

interface LineChartProps {
    width?: number
    height?: number
    category: string
    data: Sensor[]
    loading: boolean
}

type ChartDataPoint = [number, number]
interface TableRowData {
    title: string
    platform: string
    record: ChartDataPoint[]
    key: number
    installed: string
}

const LineChart: React.FC<LineChartProps> = props => {
    const { width = 600, height = 300, data, category } = props
    const [tableData, setTableData] = React.useState<TableRowData[]>()

    let pageIndexInfo = useContext(PageIndexContext)

    React.useEffect(() => {
        const tableData: TableRowData[] = []
        let firstDate: Date
        let lastDate: Date | undefined = undefined

        data.forEach((row: Sensor, index: number) => {
            let records: ChartDataPoint[] = []
            row.records.forEach((record: Record, index: number) => {
                if (index === 0) {
                    firstDate = new Date(record.recordedAt)
                    lastDate = undefined
                } else if (index === row.records.length - 1) {
                    lastDate = new Date(record.recordedAt)
                }

                let y = 0
                if (record.hasOwnProperty(category)) {
                    y = Number(record[category as keyof Record])
                } else if (record.treeForm && record.treeForm.hasOwnProperty(category)) {
                    y = record.treeForm[category as keyof Record['treeForm']]
                }

                records.push([new Date(record.recordedAt).getTime(), y])

                // Reverse records if first date > last date
                if (lastDate && firstDate > lastDate) records.reverse()
            })

            tableData.push({
                title: row.title,
                platform: row.platform,
                record: records,
                key: index,
                installed: row.installed,
            })
        })

        setTableData(tableData)
    }, [data, category])

    const download = (canvas: HTMLCanvasElement) => {
        const imgURI = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream'),
            a = document.createElement('a')
        a.setAttribute('download', `${new Date().getTime()}.png`)
        a.setAttribute('href', imgURI)
        a.setAttribute('target', '_blank')
        a.click()
    }

    const columns: TableColumnsType<TableRowData> = [
        {
            title: 'Title',
            dataIndex: 'title',
            sorter: (a: any, b: any) => (a.title > b.title ? -1 : 1),
        },
        {
            title: 'Platform',
            dataIndex: 'platform',
            filters: [
                {
                    text: 'Lora',
                    value: 'lora',
                },
                {
                    text: 'Onenet',
                    value: 'onenet',
                },
            ],
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: TableRowData) =>
                typeof value === 'string' ? record.platform.indexOf(value) === 0 : true,
        },
        {
            title: 'Installed',
            dataIndex: 'installed',
            filters: [
                {
                    text: 'Yes',
                    value: 'yes',
                },
                {
                    text: 'No',
                    value: 'no',
                },
            ],
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: TableRowData) =>
                typeof value === 'string' ? record.installed.indexOf(value) === 0 : true,
        },
        {
            title: () => {
                return (
                    <div>
                        Graph <i style={{ marginLeft: '50px' }} />
                        <Button
                            type="default"
                            icon="download"
                            onClick={() => {
                                var canvases = document.getElementsByTagName('canvas')
                                for (let i = 0; i < canvases.length; i++) {
                                    const canvas = canvases[i]
                                    download(canvas)
                                }
                            }}
                        />
                    </div>
                )
            },
            dataIndex: 'record',
            render: (record: any, row: any, index: number) => {
                const title = row.title
                const options: any = {
                    chart: {
                        width,
                        height,
                        type: 'line',
                        zoom: {
                            enabled: false,
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    stroke: {
                        // curve: 'smooth',
                        width: 3,
                    },
                    title: {
                        text: title,
                        align: 'left',
                    },
                    grid: {
                        row: {
                            colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                            opacity: 0.5,
                        },
                    },
                    markers: {
                        size: 0,
                    },
                    xaxis: {
                        type: 'datetime',
                        labels: {
                            datetimeUTC: false,
                            datetimeFormatter: {
                                year: 'yyyy',
                                month: "MMM 'yy",
                                day: 'dd MMM yy',
                                hour: 'dd MMM HH:mm',
                            },
                        },
                        tickAmount: 2,
                        min: record[0]?.[0],
                        max: record[record.length - 1]?.[0],
                    },
                    tooltip: {
                        x: {
                            show: true,
                            format: 'dd MMM yy HH:mm',
                            formatter: undefined,
                        },
                    },
                }
                return (
                    <div style={{ width, height }}>
                        <Chart
                            options={options}
                            series={[{ name: 'Data', data: record }]}
                            type="line"
                            height={height}
                            width={width}
                        />
                    </div>
                )
            },
        },
    ]

    const changePageData = (page: number, pageSize?: number) => {
        pageIndexInfo.pageIndexDispatch({
            pageIndex: page,
        })
    }

    return (
        <Table
            columns={columns}
            data={tableData}
            loading={props.loading}
            pagination={{ onChange: changePageData, total: pageIndexInfo.pageIndexState.totalPage }}
        />
    )
}

export default LineChart
